import Modal from "components/Elements/Modalv2";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import React, { FC, useEffect, useRef, useState } from "react";
import ApiService from "services/api.service";
import TokenService from "services/token.service";
import { toast } from "react-toastify";
import config, { API_BASE_URL_KEY } from "config";

interface TemplateImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ParsedTemplate {
  name: string;
  subject: string | null;
  text: string;
  include: boolean;
}

interface ParsedFile {
  filename: string;
  templates: ParsedTemplate[];
  createSequence: boolean;
  sequenceName: string;
  sequenceTags: string[];
}

type Step = "upload" | "parsing" | "preview" | "done";

const ACCEPT =
  ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const MAX_FILES = 20;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const TemplateImportModal: FC<TemplateImportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [step, setStep] = useState<Step>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [parsed, setParsed] = useState<ParsedFile[]>([]);
  const [knownTags, setKnownTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [committedSummary, setCommittedSummary] = useState<{
    templates: number;
    sequences: number;
  }>({ templates: 0, sequences: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Record<number, boolean>>(
    {}
  );

  const reset = () => {
    setStep("upload");
    setFiles([]);
    setParsed([]);
    setSubmitting(false);
    setExpandedFiles({});
    setCommittedSummary({ templates: 0, sequences: 0 });
  };

  const loadTags = async () => {
    try {
      const { data } = await ApiService.get<string[]>({
        url: `/sequences/tags`,
      });
      setKnownTags(Array.isArray(data) ? data : []);
    } catch {
      setKnownTags([]);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    reset();
    void loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAddFiles = (selected: FileList | File[]) => {
    const incoming = Array.from(selected);
    const next: File[] = [...files];
    for (const f of incoming) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (ext !== "pdf" && ext !== "docx") {
        toast.error(`${f.name}: only .pdf and .docx are supported.`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name}: file too large (max 10MB).`);
        continue;
      }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      if (next.length >= MAX_FILES) {
        toast.error(`Maximum ${MAX_FILES} files per import.`);
        break;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const handleParse = async () => {
    if (!files.length) return;
    setStep("parsing");
    try {
      const formData = new FormData();
      for (const f of files) formData.append("files", f);
      const res = await fetch(
        `${config.get(API_BASE_URL_KEY)}/templates/import/parse`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${TokenService.getLocalAccessToken()}`,
          },
        }
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Parse failed (${res.status})`);
      }
      const json = await res.json();
      const incoming: ParsedFile[] = (json.files || []).map((f: any) => {
        const baseName = (f.filename || "untitled").replace(/\.[^.]+$/, "");
        const templates: ParsedTemplate[] = (f.templates || []).map(
          (t: any) => ({
            name: t.name || "Untitled",
            subject: t.subject ?? null,
            text: t.text || "",
            include: true,
          })
        );
        return {
          filename: f.filename,
          templates,
          createSequence: templates.length >= 2,
          sequenceName: baseName,
          sequenceTags: [],
        };
      });
      setParsed(incoming);
      const expanded: Record<number, boolean> = {};
      incoming.forEach((_, i) => (expanded[i] = true));
      setExpandedFiles(expanded);
      setStep("preview");
    } catch (e: any) {
      toast.error(e?.message || "Failed to parse files");
      setStep("upload");
    }
  };

  const totalSelectedTemplates = parsed.reduce(
    (sum, f) => sum + f.templates.filter((t) => t.include).length,
    0
  );
  const totalSelectedSequences = parsed.reduce(
    (sum, f) =>
      sum +
      (f.createSequence && f.templates.filter((t) => t.include).length >= 1
        ? 1
        : 0),
    0
  );

  const handleCommit = async () => {
    if (!totalSelectedTemplates) {
      toast.error("Select at least one template to import.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await ApiService.post<{ files: any[] }>({
        url: `/templates/import/commit`,
        options: {
          files: parsed.map((f) => ({
            filename: f.filename,
            templates: f.templates.map((t) => ({
              name: t.name,
              subject: t.subject,
              text: t.text,
              include: t.include,
            })),
            createSequence: f.createSequence,
            sequenceName: f.sequenceName,
            sequenceTags: f.sequenceTags,
          })),
        },
      });
      const templates = (data?.files || []).reduce(
        (s: number, x: any) => s + (x.templateIds?.length || 0),
        0
      );
      const sequences = (data?.files || []).filter(
        (x: any) => x.sequenceId
      ).length;
      setCommittedSummary({ templates, sequences });
      setStep("done");
      window.dispatchEvent(new CustomEvent("leadstats:templates-changed"));
      window.dispatchEvent(new CustomEvent("leadstats:sequences-changed"));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to import templates");
    } finally {
      setSubmitting(false);
    }
  };

  const updateFile = (idx: number, patch: Partial<ParsedFile>) => {
    setParsed((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, ...patch } : f))
    );
  };

  const updateTemplate = (
    fileIdx: number,
    tplIdx: number,
    patch: Partial<ParsedTemplate>
  ) => {
    setParsed((prev) =>
      prev.map((f, i) =>
        i !== fileIdx
          ? f
          : {
              ...f,
              templates: f.templates.map((t, j) =>
                j === tplIdx ? { ...t, ...patch } : t
              ),
            }
      )
    );
  };

  const toggleTag = (fileIdx: number, tag: string) => {
    setParsed((prev) =>
      prev.map((f, i) =>
        i !== fileIdx
          ? f
          : {
              ...f,
              sequenceTags: f.sequenceTags.includes(tag)
                ? f.sequenceTags.filter((t) => t !== tag)
                : [...f.sequenceTags, tag],
            }
      )
    );
  };

  const renderUpload = () => (
    <div className="flex flex-col gap-4">
      <div
        className={`border-2 border-dashed rounded-md p-8 text-center transition-colors cursor-pointer ${
          dragOver
            ? "border-[#6366F1] bg-themed-background"
            : "border-themed-border"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleAddFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) handleAddFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="text-[14px] font-semibold">
          Drop .pdf or .docx files here, or click to choose
        </div>
        <div className="text-[12px] text-themed-textSecondary mt-1">
          Up to {MAX_FILES} files, 10MB each. The AI splits each file into
          individual templates.
        </div>
      </div>
      {files.length > 0 && (
        <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="flex items-center justify-between px-3 py-2 bg-themed-background rounded text-[13px]"
            >
              <div className="truncate">{f.name}</div>
              <button
                className="text-themed-textSecondary hover:text-[#F43F5E]"
                onClick={() =>
                  setFiles((prev) => prev.filter((_, j) => j !== i))
                }
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderParsing = () => (
    <div className="flex flex-col items-center justify-center gap-3 py-10">
      <div className="w-8 h-8 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
      <div className="text-[14px] text-themed-textSecondary">
        Parsing {files.length} file{files.length === 1 ? "" : "s"} with AI…
      </div>
    </div>
  );

  const renderPreview = () => (
    <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-1">
      {parsed.map((file, fi) => {
        const selectedCount = file.templates.filter((t) => t.include).length;
        const expanded = expandedFiles[fi] ?? true;
        return (
          <div key={fi} className="border border-themed-border rounded-md">
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-themed-background"
              onClick={() =>
                setExpandedFiles((prev) => ({ ...prev, [fi]: !expanded }))
              }
            >
              <div className="flex flex-col items-start gap-0.5">
                <div className="font-semibold text-[14px]">{file.filename}</div>
                <div className="text-[12px] text-themed-textSecondary">
                  {selectedCount} of {file.templates.length} template
                  {file.templates.length === 1 ? "" : "s"} selected
                  {file.createSequence && selectedCount >= 1
                    ? ` · will create sequence "${file.sequenceName}"`
                    : ""}
                </div>
              </div>
              <div className="text-[18px]">{expanded ? "▾" : "▸"}</div>
            </button>
            {expanded && (
              <div className="p-4 flex flex-col gap-4">
                {file.templates.length === 0 ? (
                  <div className="text-[13px] text-themed-textSecondary italic">
                    No templates detected in this file.
                  </div>
                ) : (
                  <>
                    {file.templates.length >= 2 && (
                      <div className="flex flex-col gap-2 p-3 rounded bg-themed-background">
                        <label className="flex items-center gap-2 text-[13px]">
                          <input
                            type="checkbox"
                            checked={file.createSequence}
                            onChange={(e) =>
                              updateFile(fi, {
                                createSequence: e.target.checked,
                              })
                            }
                          />
                          Bundle these templates into a sequence
                        </label>
                        {file.createSequence && (
                          <>
                            <div className="flex flex-col gap-1">
                              <div className="text-[12px] font-semibold text-themed-text">
                                Sequence name
                              </div>
                              <Input
                                value={file.sequenceName}
                                onChange={(v) =>
                                  updateFile(fi, { sequenceName: v })
                                }
                                placeholder="Sequence name"
                                wrapperClassName="w-full"
                                className="w-full"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <div className="text-[12px] font-semibold text-themed-text">
                                Tags{" "}
                                <span className="font-normal text-themed-textSecondary">
                                  (optional)
                                </span>
                              </div>
                              {knownTags.length === 0 ? (
                                <div className="text-[11px] text-themed-textSecondary italic">
                                  No tag options available.
                                </div>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {knownTags.map((tag) => (
                                    <button
                                      key={tag}
                                      onClick={() => toggleTag(fi, tag)}
                                      className={`px-2 py-0.5 text-[11px] rounded-full border ${
                                        file.sequenceTags.includes(tag)
                                          ? "bg-[#6366F1] text-white border-[#6366F1]"
                                          : "border-themed-border text-themed-textSecondary"
                                      }`}
                                    >
                                      {tag}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="flex flex-col gap-3">
                      {file.templates.map((t, ti) => (
                        <div
                          key={ti}
                          className={`border rounded p-3 flex flex-col gap-2 ${
                            t.include
                              ? "border-themed-border"
                              : "border-themed-border opacity-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-[13px] font-semibold">
                              <input
                                type="checkbox"
                                checked={t.include}
                                onChange={(e) =>
                                  updateTemplate(fi, ti, {
                                    include: e.target.checked,
                                  })
                                }
                              />
                              Include
                            </label>
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="text-[12px] font-semibold text-themed-text">
                              Template name
                            </div>
                            <div className="text-[11px] text-themed-textSecondary">
                              How this template shows up in your Templates list.
                            </div>
                            <Input
                              value={t.name}
                              onChange={(v) =>
                                updateTemplate(fi, ti, { name: v })
                              }
                              placeholder="Template name"
                              wrapperClassName="w-full"
                              className="w-full"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="text-[12px] font-semibold text-themed-text">
                              Email subject line{" "}
                              <span className="font-normal text-themed-textSecondary">
                                (optional)
                              </span>
                            </div>
                            <div className="text-[11px] text-themed-textSecondary">
                              What the recipient sees in their inbox subject.
                            </div>
                            <Input
                              value={t.subject ?? ""}
                              onChange={(v) =>
                                updateTemplate(fi, ti, {
                                  subject: v.trim() ? v : null,
                                })
                              }
                              placeholder="Subject line"
                              wrapperClassName="w-full"
                              className="w-full"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="text-[12px] font-semibold text-themed-text">
                              Email body
                            </div>
                            <div className="text-[11px] text-themed-textSecondary">
                              The message that gets sent. Placeholders like{" "}
                              <code className="px-1 bg-themed-card rounded">
                                {`{{ firstName }}`}
                              </code>{" "}
                              fill in each contact&apos;s real info at send
                              time.
                            </div>
                            <textarea
                              className="w-full min-h-[120px] p-2 text-[12px] bg-themed-background border border-themed-border rounded font-mono"
                              value={t.text}
                              onChange={(e) =>
                                updateTemplate(fi, ti, {
                                  text: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderDone = () => (
    <div className="flex flex-col items-center justify-center gap-3 py-8">
      <div className="text-[16px] font-semibold">Import complete</div>
      <div className="text-[14px] text-themed-textSecondary text-center">
        Created {committedSummary.templates} template
        {committedSummary.templates === 1 ? "" : "s"}
        {committedSummary.sequences > 0
          ? ` and ${committedSummary.sequences} sequence${
              committedSummary.sequences === 1 ? "" : "s"
            }`
          : ""}
        .
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelClassName="w-[760px] max-w-[95vw] max-h-[90vh] px-[32px] pt-[32px] pb-[24px] flex flex-col"
    >
      <div className="flex flex-col gap-5 font-inter text-[14px] text-themed-text">
        <div className="flex items-center justify-between">
          <div className="text-[20px] font-semibold leading-[28px]">
            Import templates
          </div>
          <div className="text-[12px] text-themed-textSecondary">
            {step === "upload"
              ? "Step 1 of 3 — upload"
              : step === "parsing"
              ? "Step 2 of 3 — parsing"
              : step === "preview"
              ? "Step 3 of 3 — preview"
              : "Done"}
          </div>
        </div>

        {step === "upload" && renderUpload()}
        {step === "parsing" && renderParsing()}
        {step === "preview" && renderPreview()}
        {step === "done" && renderDone()}

        <div className="flex justify-end gap-2 pt-2 border-t border-themed-border">
          {step === "upload" && (
            <>
              <Button type={ButtonType.SECONDARY} onClick={onClose}>
                Cancel
              </Button>
              <Button
                type={ButtonType.PRIMARY}
                onClick={handleParse}
                disabled={!files.length}
              >
                Parse {files.length || ""} file{files.length === 1 ? "" : "s"}
              </Button>
            </>
          )}
          {step === "parsing" && (
            <Button type={ButtonType.SECONDARY} onClick={onClose} disabled>
              Working…
            </Button>
          )}
          {step === "preview" && (
            <>
              <Button
                type={ButtonType.SECONDARY}
                onClick={() => setStep("upload")}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                type={ButtonType.PRIMARY}
                onClick={handleCommit}
                disabled={submitting || totalSelectedTemplates === 0}
              >
                {submitting
                  ? "Importing…"
                  : `Import ${totalSelectedTemplates} template${
                      totalSelectedTemplates === 1 ? "" : "s"
                    }${
                      totalSelectedSequences > 0
                        ? ` + ${totalSelectedSequences} sequence${
                            totalSelectedSequences === 1 ? "" : "s"
                          }`
                        : ""
                    }`}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button type={ButtonType.PRIMARY} onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TemplateImportModal;
